import { useState, useEffect } from "react";
import api, { isOrganizationScopeCancel, settleOrganizationScopeCancel } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { 
  FileText, Download, Mail, Upload, FileSpreadsheet, 
  AlertTriangle, CheckCircle, Loader2, Building2,
  Users, Plane, Battery, FolderOpen
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export const Reports = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const {
    activeOrganizationId,
    clearScope,
    isPlatformAdmin,
    organizations,
    registerScopeChangeGuard,
    setActiveOrganization,
  } = useOrganizationScope();
  const selectedOrg = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const [loading, setLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importType, setImportType] = useState("pilots");
  const [csvContent, setCsvContent] = useState("");
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    setImportResult(null);
    setCsvContent("");
  }, [activeOrganizationId]);

  useEffect(() => {
    if (!csvContent.trim()) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.reports?.scopeChangeConfirm));
  }, [csvContent, registerScopeChangeGuard, t.reports?.scopeChangeConfirm]);

  const downloadReport = async (reportType) => {
    if (!selectedOrg) {
      toast.error(t.reports?.selectOrgError || "Please select an organization");
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(
        `/reports/${reportType}/${selectedOrg}`,
        settleOrganizationScopeCancel({ responseType: 'blob' })
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${reportType}_report.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(t.reports?.reportDownloaded || "Report downloaded successfully");
    } catch (error) {
      if (isOrganizationScopeCancel(error)) return;
      toast.error(t.reports?.reportError || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSVTemplate = async (entityType) => {
    try {
      const response = await api.get(
        `/csv/template/${entityType}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entityType}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t.reports?.templateDownloaded || "Template downloaded");
    } catch (error) {
      toast.error(t.reports?.templateError || "Failed to download template");
    }
  };

  const exportCSV = async (entityType) => {
    try {
      const params = isPlatformAdmin && selectedOrg ? `?organization_id=${selectedOrg}` : "";
      const response = await api.get(
        `/csv/export/${entityType}${params}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entityType}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t.reports?.exportSuccess || "Data exported successfully");
    } catch (error) {
      toast.error(t.reports?.exportError || "Failed to export data");
    }
  };

  const handleImport = async () => {
    if (!selectedOrg) {
      toast.error(t.reports?.selectOrgError || "Please select an organization");
      return;
    }

    if (!csvContent.trim()) {
      toast.error(t.reports?.pasteCsvError || "Please paste CSV content");
      return;
    }

    setLoading(true);
    setImportResult(null);
    
    try {
      const response = await api.post(
        '/csv/import',
        {
          entity_type: importType,
          organization_id: selectedOrg,
          csv_content: csvContent
        },
        settleOrganizationScopeCancel()
      );

      setImportResult(response.data);

      if (response.data.status === "success") {
        toast.success((t.reports?.importSuccess || "Imported {count} records").replace("{count}", response.data.imported));
      } else {
        toast.error(t.reports?.importFailedCheckErrors || "Import failed - check errors below");
      }
    } catch (error) {
      if (isOrganizationScopeCancel(error)) return;
      toast.error(error.response?.data?.detail || t.reports?.importError || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const sendAlerts = async () => {
    if (!selectedOrg) {
      toast.error(t.reports?.selectOrgError || "Please select an organization");
      return;
    }

    setAlertsLoading(true);
    try {
      const response = await api.post(
        `/alerts/check/${selectedOrg}`,
        {},
        settleOrganizationScopeCancel()
      );

      toast.success((t.reports?.alertsSent || "Sent {count} alert(s)").replace("{count}", response.data.alerts_sent));
    } catch (error) {
      if (isOrganizationScopeCancel(error)) return;
      if (error.response?.data?.detail?.includes("not configured")) {
        toast.error(t.reports?.emailNotConfigured || "Email service not configured. Add RESEND_API_KEY to enable.");
      } else {
        toast.error(t.reports?.alertsError || "Failed to send alerts");
      }
    } finally {
      setAlertsLoading(false);
    }
  };

  const entityTypes = [
    { value: "pilots", label: "Pilots", icon: Users },
    { value: "aircraft", label: "Aircraft", icon: Plane },
    { value: "batteries", label: "Batteries", icon: Battery },
    { value: "documents", label: "Documents", icon: FolderOpen }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Reports & Data
          </h1>
          <p className="text-slate-500 mt-1">Generate reports and manage data imports/exports</p>
        </div>
      </div>

      {/* Organization Selector */}
      {isPlatformAdmin && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Building2 className="h-5 w-5 text-slate-400" />
              <div className="flex-1">
                <Label className="text-sm text-slate-500">Select Organization</Label>
                <Select
                  value={selectedOrg || "__all__"}
                  onValueChange={(value) => value === "__all__" ? clearScope() : setActiveOrganization(value)}
                >
                  <SelectTrigger className="mt-1" data-testid="org-selector">
                    <SelectValue placeholder="Choose an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{t.organizationScope?.allOrganizations}</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="reports" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="reports" data-testid="tab-reports">PDF Reports</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export">Export Data</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">Import Data</TabsTrigger>
        </TabsList>

        {/* PDF Reports Tab */}
        <TabsContent value="reports" className="space-y-4 mt-6">
          {!selectedOrg && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              {t.reports?.scopeRequired}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Compliance Report */}
            <Card className="card-hover">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-sky-100">
                    <FileText className="h-5 w-5 text-sky-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Compliance Report</CardTitle>
                    <CardDescription>Monthly compliance summary</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Generate a professional PDF report including compliance score, violations, 
                  expiring certifications, maintenance status, and open incidents.
                </p>
                <ul className="text-xs text-slate-500 space-y-1 mb-4">
                  <li>• Compliance score & status summary</li>
                  <li>• RED and YELLOW violations</li>
                  <li>• Expiring pilot certifications</li>
                  <li>• Aircraft maintenance alerts</li>
                  <li>• Open incidents</li>
                </ul>
                <Button 
                  onClick={() => downloadReport('compliance')}
                  disabled={loading || !selectedOrg}
                  className="w-full gap-2 bg-primary hover:bg-primary/90"
                  data-testid="download-compliance-report"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Download Report
                </Button>
              </CardContent>
            </Card>

            {/* Audit Export */}
            <Card className="card-hover">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Audit Export</CardTitle>
                    <CardDescription>Comprehensive audit package</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Generate a complete audit-ready PDF with all organizational data, 
                  full compliance history, and detailed operational records.
                </p>
                <ul className="text-xs text-slate-500 space-y-1 mb-4">
                  <li>• Full organization details</li>
                  <li>• All pilots & certifications</li>
                  <li>• Complete aircraft fleet</li>
                  <li>• All documentation</li>
                  <li>• Mission & incident history</li>
                </ul>
                <Button 
                  onClick={() => downloadReport('audit')}
                  disabled={loading || !selectedOrg}
                  className="w-full gap-2 bg-primary hover:bg-primary/90"
                  data-testid="download-audit-report"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Download Audit Export
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Email Alerts Section */}
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Mail className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Email Alerts</CardTitle>
                  <CardDescription>Send compliance alerts to organization admins</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Check for expiring certifications, overdue maintenance, and open incidents, 
                then send email alerts to organization administrators and compliance managers.
              </p>
              <div className="flex items-center gap-4">
                <Button 
                  onClick={sendAlerts}
                  disabled={alertsLoading || !selectedOrg}
                  variant="outline"
                  className="gap-2"
                  data-testid="send-alerts-btn"
                >
                  {alertsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                  Check & Send Alerts
                </Button>
                <span className="text-xs text-slate-400">
                  Sends alerts for items expiring within 60 days
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {entityTypes.map(({ value, label, icon: Icon }) => (
              <Card key={value} className="card-hover">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <Icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <span className="font-medium">{label}</span>
                  </div>
                  <div className="space-y-2">
                    <Button 
                      onClick={() => exportCSV(value)}
                      variant="outline"
                      className="w-full gap-2"
                      data-testid={`export-${value}`}
                    >
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                    <Button 
                      onClick={() => downloadCSVTemplate(value)}
                      variant="ghost"
                      className="w-full gap-2 text-slate-500"
                      data-testid={`template-${value}`}
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Get Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Import Tab */}
        <TabsContent value="import" className="space-y-4 mt-6">
          {!selectedOrg && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              {t.reports?.scopeRequired}
            </div>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Import Data from CSV</CardTitle>
              <CardDescription>
                Bulk import pilots, aircraft, batteries, or documents from a CSV file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Data Type</Label>
                    <Select value={importType} onValueChange={setImportType}>
                      <SelectTrigger className="mt-1" data-testid="import-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {entityTypes.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={() => downloadCSVTemplate(importType)}
                      variant="outline"
                      className="gap-2"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Download Template
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>CSV Content</Label>
                  <Textarea
                    value={csvContent}
                    onChange={(e) => setCsvContent(e.target.value)}
                    placeholder="Paste your CSV content here..."
                    rows={10}
                    className="mt-1 font-mono text-sm"
                    data-testid="csv-content"
                  />
                </div>

                <Button 
                  onClick={handleImport}
                  disabled={loading || !selectedOrg || !csvContent.trim()}
                  className="gap-2 bg-primary hover:bg-primary/90"
                  data-testid="import-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Import Data
                </Button>

                {importResult && (
                  <div className={`p-4 rounded-lg ${importResult.status === 'success' ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {importResult.status === 'success' ? (
                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-rose-600" />
                      )}
                      <span className={`font-medium ${importResult.status === 'success' ? 'text-emerald-800' : 'text-rose-800'}`}>
                        {importResult.message}
                      </span>
                    </div>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <ul className="text-sm text-rose-700 mt-2 space-y-1">
                        {importResult.errors.map((error, i) => (
                          <li key={`import-error-${error.substring(0, 20).replace(/\s+/g, '-')}-${i}`}>• {error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
